import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { useSidebar } from "../../SidebarContext";
import Header from "../Header";
import Sidebar from "../SideBar";
import { User, StickyNote, ArrowLeftIcon, PenIcon, Trash2Icon, FileText } from "lucide-react";
import { decryptToken } from "../utils/getToken";

const InvoiceDetail = () => {
    const { isSidebarOpen } = useSidebar();
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const navigate = useNavigate();
    const url = process.env.REACT_APP_LINK;
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""

    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const res = await fetch(`${url}/newinvoice/singleinvoice/${id}`,{
                    method:"GET",
                    headers: {
                    Authorization: `Bearer ${token}`,
                }
                });
                const result = await res.json();
                console.log("Single Invoice", result);
                setInvoice(result);
            } catch (error) {
                console.error("Error loading invoice:", error);
                setInvoice(null);
            }
        };

        if (id) fetchInvoice();
    }, [id]);

    if (!invoice) return <p>Loading invoice details...</p>;

    const handleEdit = () => {
        navigate('/new-invoice', { state: { invoice } });
    };

    const handleDelete = async (invoiceId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'This invoice will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/newinvoice/${invoiceId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });

                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || 'Failed to delete');
                    }

                    Swal.fire('Deleted!', 'Invoice has been deleted.', 'success');
                    navigate('/invoice');
                } catch (error) {
                    console.error('Delete error:', error.message);
                    Swal.fire('Error!', `Failed to delete invoice. ${error.message}`, 'error');
                }
            }
        });
    };


    let parsedItems = [];
    try {
        if (invoice.items && typeof invoice.items === 'string') {
            parsedItems = JSON.parse(invoice.items);
        }
    } catch (e) {
        console.error("Failed to parse items:", e);
    }

    const getStatusStyle = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'pending':
                return { backgroundColor: '#f0f0f0', color: '#333' };
            case 'paid':
                return { backgroundColor: '#d4edda', color: '#155724' };
            case 'overdue':
                return { backgroundColor: '#f8d7da', color: '#721c24' };
            default:
                return {};
        }
    };


    console.log(parsedItems)

    return (
        <>

            <div className={`open-close ${isSidebarOpen ? "open" : "closed"}`}>
                <div className="ecom-customer-detail-container">
                    <div className="ecom-customer-header-detail">
                        <div className="ecom-customer-header-left-container">
                            <ArrowLeftIcon onClick={() => navigate('/invoice')} />
                            <h2 className="ecom-customer-name-highlight">
                                #{invoice.invoice_number} {invoice.status && (
                                    <span
                                        style={{
                                            ...getStatusStyle(invoice.status),
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            marginLeft: 8,
                                        }}
                                    >
                                        {invoice.status}
                                    </span>
                                )}
                            </h2>
                        </div>
                        <div className="ecom-customer-header-right-container">
                            <PenIcon onClick={handleEdit} />
                            <Trash2Icon onClick={() => handleDelete(invoice.id)} />
                        </div>
                    </div>

                    {/* 1. Invoice Details */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><User size={16} style={{ marginRight: '8px' }} /> Invoice Details</h4>
                        <div className="ecom-customer-detail-grid">
                            <div><label>Order No:</label><span>{invoice.order_number}</span></div>
                            <div><label>Invoice Date:</label><span>{invoice.invoice_date}</span></div>
                            <div><label>Due Date:</label><span>{invoice.due_date}</span></div>
                            <div><label>Terms:</label><span>{invoice.terms || "—"}</span></div>
                            <div><label>Due Amount:</label><span>₹ {invoice.pending}</span></div>
                        </div>
                    </section>

                    {/* 2. Items Detail */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><FileText size={16} style={{ marginRight: '8px' }} /> Items Detail</h4>
                        {parsedItems.length > 0 ? (
                            <div style={{ paddingLeft: '12px' }}>
                                {parsedItems
                                    .filter(i => i.item || i.quantity || i.rate || i.amount !== "0.00")
                                    .map((item, index) => {
                                        const qty = parseFloat(item.quantity) || 0;
                                        const rate = parseFloat(item.rate) || 0;
                                        const total = (qty * rate);

                                        return (
                                            <div key={index} style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 10 }}>
                                                <div><strong>Item:</strong> {item.item?.title || "—"}</div>
                                                <div><strong>Quantity × Rate:</strong> {item.quantity || "0"} × ₹{item.rate || "0.00"} = ₹{total}</div>
                                            </div>
                                        );
                                    })}

                                <div><strong>Subtotal:</strong> ₹ {invoice.subtotal}</div>
                                <div><strong>Discount:</strong> ₹ {invoice.discount_amount}</div>
                                <div><strong>Advance Payment:</strong> ₹ {invoice.advance_payment}</div>
                                <div><strong>Tax:</strong> ₹ {invoice.tax}</div>
                                <div><strong>Shipping / Service Fees:</strong> ₹ {invoice.shipping}</div>
                                <div><strong>Grand Total:</strong> ₹ {invoice.grand_total}</div>
                            </div>
                        ) : (
                            <p style={{ textAlign: "center", margin: "20px 10px" }}>No items found for this invoice.</p>
                        )}
                    </section>

                    {/* 3. Customer Note */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><StickyNote size={16} style={{ marginRight: '8px' }} /> Customer Note</h4>
                        <div dangerouslySetInnerHTML={{ __html: invoice.customer_note || "<p>—</p>" }} />
                    </section>
                </div>
            </div>
        </>
    );
};

export default InvoiceDetail;
